import { useCallback, useMemo, useRef } from 'react'

import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { generatePath } from 'react-router'
import { CallStart } from 'src/components/icon'
import { InformationMessage } from 'src/components/InformationMessage/InformationMessage'
import { Paths } from 'src/constants/paths'
import { useSelectedTrial } from 'src/features/auth/context'
import { useModal } from 'src/hooks/use-modal'
import { ModalCancelButton } from 'src/lib/chakra-theme/components'
import {
  ExplanationType,
  ExplanationVideoCallButton_ReservationFragment,
  GetExplanationDetailQuery,
} from 'src/lib/gql-client'
import { assertNever } from 'src/utils/assertNever'
import { formatDate } from 'src/utils/formatDate'
import { openNewWindow } from 'src/utils/openNewWindow'

type Props = {
  trialPatient: GetExplanationDetailQuery['explanation']['patient']['trialPatient']
  isPartnerMember?: boolean
  trialPatientUid: string
  mainHospitalName: string
  partnerHospitalName?: string
  explanationType: ExplanationType
  candidateId: string
}

type Target = 'PartnerHospital' | 'Patient' | 'MainHospital'

const getTargets = (
  explanationType: ExplanationType,
  isPartnerMember?: boolean,
): Target[] => {
  switch (explanationType) {
    case 'InPerson':
      return ['Patient']
    case 'Remote':
      return ['Patient']
    case 'RemotePartner':
      return isPartnerMember ? ['MainHospital'] : ['PartnerHospital', 'Patient']
    default:
      return []
  }
}

const getTrialPatientVideoCallPath = (param: {
  trialUid: string
  trialPatientUid: string
  inviteeType: 'Patient' | 'PartnerMembers'
}) => {
  const { trialUid, trialPatientUid } = param
  const searchParams = new URLSearchParams()
  searchParams.set('inviteeType', param.inviteeType)
  return [
    generatePath(Paths.HospitalTrialPatientVideoCall, {
      trialUid,
      trialPatientUid,
    }),
    searchParams.toString(),
  ].join('?')
}

export const ExplanationVideoCallButton: React.FC<Props> = ({
  trialPatient,
  isPartnerMember,
  trialPatientUid,
  mainHospitalName,
  partnerHospitalName,
  explanationType,
  candidateId,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()

  const { selectedTrial } = useSelectedTrial()

  const targets = getTargets(explanationType, isPartnerMember)

  const { isOpen: isConfirmationModalOpen, showModal } = useModal()

  const { reservations } = trialPatient

  const activeReservation = useMemo(() => {
    return reservations?.filter(r => r.timeStatus === 'Upcoming')?.[0]
  }, [reservations])

  const getTargetStr = useCallback(
    (target: Target) => {
      switch (target) {
        case 'MainHospital':
          return mainHospitalName
        case 'PartnerHospital':
          return partnerHospitalName
        case 'Patient':
          return '患者御本人'
        default:
          return assertNever(target)
      }
    },
    [mainHospitalName, partnerHospitalName],
  )

  // onSuccessで実行する際に参照するため、refで保持
  const inviteeRef = useRef<'Patient' | 'PartnerMembers'>('Patient')

  const windowType = isPartnerMember ? 'tab' : 'newWindow'

  // 非同期処理を行うため、新しいウィンドウを開いておく。こうしないとpopup blockに引っかかる場合がある
  // let preparedWindow: Window | null
  const preparedWindow = useRef<Window | null>(null)

  const handleStartCall = useCallback(
    async (
      target: Target,
      myReservation: ExplanationVideoCallButton_ReservationFragment | undefined,
    ) => {
      inviteeRef.current = target === 'Patient' ? 'Patient' : 'PartnerMembers'
      const openVideoCallWindow = () => {
        preparedWindow.current = openNewWindow('', windowType)
        const path = getTrialPatientVideoCallPath({
          trialUid: selectedTrial.uid,
          trialPatientUid,
          inviteeType: inviteeRef.current,
        })
        if (preparedWindow.current) {
          preparedWindow.current.location.href = path
        }
        onClose()
      }
      // 実施医療機関ユーザーが次回予約日時が設定されていない方の案内先（パートナー施設or患者）ボタン押下時には確認メッセージを表示し、
      // 通話をこのまま実施するかを確認する。
      if (!isPartnerMember && activeReservation && !myReservation) {
        showModal({
          title: 'ビデオ通話の開始',
          size: '2xl',
          content: (
            <Stack>
              <Text>
                予約日時は設定されていませんが、{getTargetStr(target)}
                と通話を開始しますか？
              </Text>
              {target === 'Patient' && (
                <InformationMessage message="ビデオ通話開始時に即時で患者宛に案内メールが送信されます。" />
              )}
            </Stack>
          ),
          submitText: '通話を開始する',
          onSubmit: openVideoCallWindow,
        })
      } else {
        openVideoCallWindow()
      }
    },
    [
      selectedTrial.uid,
      activeReservation,
      isPartnerMember,
      windowType,
      trialPatientUid,
      onClose,
      showModal,
      getTargetStr,
    ],
  )

  return (
    <>
      <Button variant="outline" onClick={onOpen}>
        ビデオ通話を開始
      </Button>

      <Modal
        isOpen={isOpen && !isConfirmationModalOpen}
        onClose={onClose}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>ビデオ通話を開始します</ModalHeader>
          <ModalBody>
            <Stack spacing="6">
              {targets.length > 1 && <Text>通話先を選択してください。</Text>}

              <Stack spacing="4">
                {targets.includes('PartnerHospital') && (
                  <Button
                    name={`${partnerHospitalName}と通話`}
                    variant={
                      activeReservation?.explanationReservation
                        ?.reservationInviteeType === 'PartnerMembers'
                        ? 'solid'
                        : 'outline'
                    }
                    w="full"
                    leftIcon={<CallStart size="32px" />}
                    display="flex"
                    alignItems="center"
                    justifyContent="start"
                    minH="16"
                    h="auto"
                    onClick={async () => {
                      const reservation =
                        activeReservation?.explanationReservation
                          ?.reservationInviteeType === 'PartnerMembers'
                          ? activeReservation
                          : undefined
                      await handleStartCall('PartnerHospital', reservation)
                    }}
                  >
                    <Stack spacing="0.5" h="full" justify="center">
                      <Text as="span" fontWeight="bold" fontSize="md">
                        {partnerHospitalName}
                      </Text>
                      {activeReservation?.explanationReservation
                        ?.reservationInviteeType === 'PartnerMembers' && (
                        <Text as="span" fontSize="sm">
                          予約日時:
                          {formatDate(
                            activeReservation.reservedAt,
                            'YYYY/MM/DD (ddd) HH:mm',
                          )}{' '}
                          ~
                        </Text>
                      )}
                    </Stack>
                  </Button>
                )}

                {targets.includes('MainHospital') && (
                  <Button
                    name={`${mainHospitalName}と通話`}
                    variant={
                      activeReservation?.explanationReservation
                        ?.reservationInviteeType === 'PartnerMembers'
                        ? 'solid'
                        : 'outline'
                    }
                    w="full"
                    leftIcon={<CallStart size="32px" />}
                    display="flex"
                    alignItems="center"
                    justifyContent="start"
                    minH="16"
                    h="auto"
                    onClick={async () => {
                      const reservation =
                        activeReservation?.explanationReservation
                          ?.reservationInviteeType === 'PartnerMembers'
                          ? activeReservation
                          : undefined
                      await handleStartCall('MainHospital', reservation)
                    }}
                  >
                    <Stack spacing="0.5" h="full" justify="center">
                      <Text as="span" fontWeight="bold" fontSize="md">
                        {mainHospitalName}
                      </Text>
                      {activeReservation?.explanationReservation
                        ?.reservationInviteeType === 'PartnerMembers' && (
                        <Text as="span" fontSize="sm">
                          予約日時:
                          {formatDate(
                            activeReservation.reservedAt,
                            'YYYY/MM/DD (ddd) HH:mm',
                          )}{' '}
                          ~
                        </Text>
                      )}
                    </Stack>
                  </Button>
                )}

                {targets.includes('Patient') && (
                  <Button
                    name="患者本人と通話"
                    w="full"
                    variant={
                      activeReservation?.explanationReservation
                        ?.reservationInviteeType === 'Patient'
                        ? 'solid'
                        : 'outline'
                    }
                    leftIcon={<CallStart size="32px" />}
                    display="flex"
                    alignItems="center"
                    justifyContent="start"
                    minH="16"
                    h="auto"
                    onClick={async () => {
                      const reservation =
                        activeReservation?.explanationReservation
                          ?.reservationInviteeType === 'Patient'
                          ? activeReservation
                          : undefined
                      await handleStartCall('Patient', reservation)
                    }}
                  >
                    <Stack spacing="0.5" h="full" justify="center">
                      <HStack as="span" fontWeight="bold" fontSize="md">
                        <Text as="span" wordBreak="keep-all">
                          患者ご本人
                        </Text>
                        <Text as="span" wordBreak="keep-all">
                          候補ID: {candidateId}
                        </Text>
                      </HStack>
                      {activeReservation?.explanationReservation
                        ?.reservationInviteeType === 'Patient' && (
                        <Text as="span" fontSize="sm">
                          予約日時:
                          {formatDate(
                            activeReservation.reservedAt,
                            'YYYY/MM/DD (ddd) HH:mm',
                          )}{' '}
                          ~
                        </Text>
                      )}
                    </Stack>
                  </Button>
                )}
              </Stack>
            </Stack>
          </ModalBody>
          <ModalFooter justifyContent="center">
            <ModalCancelButton />
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
